import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { BulkRecurrenceDeleteDialog } from './BulkRecurrenceDeleteDialog';
import { BulkRecurrenceEditDialog } from './BulkRecurrenceEditDialog';
import { FollowingGeneratedEditDialog } from './FollowingGeneratedEditDialog';
import type { CardDetail } from '@/entities/card';
import {
  defaultDraft,
  describeRule,
  draftFromRule,
  exceptionConflicts,
  needsExceptionPolicy,
  RecurrenceFields,
  type RecurrenceRule,
  type SaveExtra,
  templateEdit,
  useDeleteRecurrence,
  usePauseRecurrence,
  usePreviewRecurrence,
  useRecurrence,
  useSaveRecurrence,
  validateRecurrence,
  useOccurrenceExceptions,
  useResetOccurrence,
} from '@/entities/recurrence';
import { useViewer } from '@/entities/viewer';
import { FormError } from '@/shared/forms';
import { formatCalendarDate } from '@/shared/lib/datetime';
import {
  Button,
  ConfirmDialog,
  ErrorState,
  HelpRow,
  LoadingState,
  OverdueToggle,
  TextField,
  TimeField,
} from '@/shared/ui';

import { ExceptionPolicyDialog, type PolicyConflict } from './ExceptionPolicyDialog';
import styles from './Recurrence.module.css';

interface Props {
  card: CardDetail;
  canEdit: boolean;
  onDeleteThis?: () => void;
  onBulkDeleted?: () => void;
  /**
   * `card`: the card's own dialog; saving also re-takes the series template from this card.
   * `series`: the Calendar's series editor; the stored template (title, times, length) is edited
   * here instead, and the card's current content is not copied over it.
   */
  mode?: 'card' | 'series';
}

/**
 * Recurrence of the card. A task or event becomes the SOURCE of a series: new cards are made from
 * the series template on schedule (independent of whether earlier ones are done), and upcoming
 * occurrences are shown in the Calendar before they exist. A card made by a series shows that,
 * read-only. The rule can be edited, paused, resumed and stopped; editing it never changes cards
 * that were already made, and upcoming individual changes are kept or reset on request.
 */
export function RecurrenceSection({
  card,
  canEdit,
  mode = 'card',
  onDeleteThis,
  onBulkDeleted,
}: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const viewer = useViewer();
  const viewerZone = viewer.status === 'authenticated' ? viewer.user.timezone : null;
  const info = useRecurrence(card.id);
  const save = useSaveRecurrence(card.id);
  const preview = usePreviewRecurrence(card.id);
  const pause = usePauseRecurrence(card.id);
  const stop = useDeleteRecurrence(card.id);
  const startDefault = card.deadline?.start_date ?? card.deadline?.date ?? '';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => defaultDraft(startDefault));
  const [errors, setErrors] = useState<ReturnType<typeof validateRecurrence>>([]);
  const [confirmStop, setConfirmStop] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [instanceBulkScope, setInstanceBulkScope] = useState<'following' | 'series' | null>(null);
  const [followingEditOpen, setFollowingEditOpen] = useState(false);
  const [template, setTemplate] = useState({
    title: '',
    startTime: '',
    endTime: '',
    length: '0',
    overdue: false,
  });
  // Saving needs a decision about the series' individual changes (policy / conflicts dialog).
  const [policy, setPolicy] = useState<{ count: number; request: SaveExtra } | null>(null);
  const [conflicts, setConflicts] = useState<PolicyConflict[] | null>(null);

  const describe = (rule: RecurrenceRule): string => describeRule(rule, t, i18n.language);

  const heading = (
    <HelpRow topic={t('recurrence.title')} topicId="recurring" text={t('help.recurrence')}>
      <h3 id={`recurrence-${card.id}`} className={styles.title}>
        {t('recurrence.title')}
      </h3>
    </HelpRow>
  );
  const frame = (children: React.ReactNode) => (
    <section
      className={styles.section}
      aria-labelledby={`recurrence-${card.id}`}
      data-testid="recurrence"
    >
      {heading}
      {children}
    </section>
  );

  // Loading and failure are shown, never mistaken for "no recurrence".
  if (info.isPending) return frame(<LoadingState />);
  if (info.isError) {
    return frame(
      <div data-testid="recurrence-error">
        <p className={styles.warn} role="alert">
          {t('recurrence.loadError')}
        </p>
        <ErrorState
          error={info.error}
          onRetry={() => {
            void info.refetch();
          }}
        />
      </div>,
    );
  }
  const { role, rule } = info.data;
  // The zone of an existing series never changes by editing it; a new one follows its card.
  const timezone = rule?.timezone ?? card.deadline?.timezone ?? viewerZone;

  const startEdit = () => {
    setDraft(rule ? draftFromRule(rule) : defaultDraft(startDefault || todayLocal(viewerZone)));
    setTemplate({
      title: rule?.title ?? '',
      startTime: rule?.start_time?.slice(0, 5) ?? '',
      endTime: rule?.end_time?.slice(0, 5) ?? '',
      length: String(rule?.length_days ?? 0),
      overdue: rule?.overdue_enabled === true,
    });
    setErrors([]);
    setEditing(true);
  };

  const closeAll = () => {
    setEditing(false);
    setPolicy(null);
    setConflicts(null);
  };

  const submit = (extra: SaveExtra = {}) => {
    const found = validateRecurrence(draft);
    setErrors(found);
    if (found.length > 0) return;
    const length = Number(template.length);
    const request: SaveExtra = {
      ...(rule && { expectedVersion: rule.version }),
      ...(mode === 'card'
        ? { refreshFromSource: true }
        : rule && {
            template: templateEdit(rule, {
              title: template.title,
              start_time: template.startTime,
              end_time: template.endTime,
              ...(rule.is_event && Number.isInteger(length) && { length_days: length }),
              ...(rule.is_event && { overdue_enabled: template.overdue }),
            }),
          }),
      ...extra,
    };
    save.mutate(
      { draft, timezone, ...request },
      {
        onSuccess: closeAll,
        onError: (error) => {
          const count = needsExceptionPolicy(error);
          if (count !== null) {
            setPolicy({ count, request });
            setConflicts(null);
            preview.mutate({ draft, timezone, ...request, exceptions: 'preserve' });
            return;
          }
          const list = exceptionConflicts(error);
          if (list.length > 0) setConflicts(list);
        },
      },
    );
  };

  const needsDecision = policy !== null || conflicts !== null;
  const saveError = needsDecision ? null : save.error;
  const error = saveError ?? pause.error ?? stop.error;
  const ended = rule?.status === 'ended';

  return frame(
    <>
      {role === 'instance' && rule && (
        <div className={styles.summary}>
          <p className={styles.note}>
            {t('recurrence.instance')} <strong>{describe(rule)}</strong>
          </p>

          <div className={styles.actions}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                void navigate(`/app/boards/${rule.board_id}/cards/${rule.source_card_id}`);
              }}
            >
              {t('recurrence.openSourceCard')}
            </Button>

            {onDeleteThis && (
              <Button size="sm" variant="ghost" onClick={onDeleteThis}>
                {t('recurrence.bulk.onlyThis')}
              </Button>
            )}

            {canEdit &&
              info.data.original_date &&
              info.data.original_date >= todayLocal(rule.timezone) && (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setInstanceBulkScope('following');
                  }}
                >
                  {t('recurrence.bulk.followingFromThis')}
                </Button>
              )}

            {canEdit &&
              rule.status === 'active' &&
              info.data.original_date &&
              info.data.original_date >= todayLocal(rule.timezone) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setFollowingEditOpen(true);
                  }}
                >
                  {t('recurrence.followingEdit.open')}
                </Button>
              )}

            {canEdit && (
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  setInstanceBulkScope('series');
                }}
              >
                {t('recurrence.bulk.allFuture')}
              </Button>
            )}
          </div>
        </div>
      )}

      {role === 'source' && rule && !editing && (
        <div className={styles.summary}>
          <p className={styles.line}>
            <strong>{describe(rule)}</strong>
            <span
              className={`${styles.status} ${styles[rule.status]}`}
              data-testid="recurrence-status"
            >
              {t(`recurrence.status.${rule.status}`)}
            </span>
          </p>
          {rule.status !== 'active' && rule.paused_reason && (
            <p className={styles.warn} role="status">
              {t(`recurrence.reason.${rule.paused_reason}`)}
            </p>
          )}
          {rule.failure_count > 0 && (
            <p className={styles.failed} role="status">
              {t('recurrence.failures', { count: rule.failure_count })}
            </p>
          )}
          {rule.next_occurrence_date && (
            <p className={styles.note}>
              {t('recurrence.next', {
                date: formatCalendarDate(rule.next_occurrence_date, i18n.language),
              })}
            </p>
          )}
          {rule.generated_count > 0 && (
            <p className={styles.note}>
              {t('recurrence.created', { count: rule.generated_count })}
            </p>
          )}
          {rule.exception_count > 0 && (
            <div data-testid="recurrence-exceptions">
              <RecurrenceExceptions
                cardId={card.id}
                count={rule.exception_count}
                canEdit={canEdit}
              />
            </div>
          )}
          {canEdit && (
            <div className={styles.actions}>
              <Button size="sm" onClick={startEdit}>
                {ended ? t('recurrence.restart') : t('recurrence.edit')}
              </Button>
              {rule.status === 'active' && (
                <Button
                  size="sm"
                  disabled={pause.isPending}
                  onClick={() => {
                    pause.mutate(true);
                  }}
                >
                  {t('recurrence.pause')}
                </Button>
              )}
              {rule.status === 'paused' && rule.paused_reason !== 'needs_review' && (
                <Button
                  size="sm"
                  variant="primary"
                  disabled={pause.isPending}
                  onClick={() => {
                    pause.mutate(false);
                  }}
                >
                  {t('recurrence.resume')}
                </Button>
              )}
              {!ended && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setConfirmStop(true);
                  }}
                >
                  {t('recurrence.stop')}
                </Button>
              )}
              {!ended && (
                <Button
                  size="sm"
                  variant="danger"
                  className={styles.destructive}
                  onClick={() => {
                    setConfirmBulkDelete(true);
                  }}
                >
                  {t('recurrence.bulk.open')}
                </Button>
              )}
              {!ended && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setBulkEditOpen(true);
                  }}
                >
                  {t('recurrence.bulkEdit.open')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {role === null && !editing && canEdit && (
        <div>
          <Button size="sm" onClick={startEdit}>
            {t('recurrence.set')}
          </Button>
        </div>
      )}

      {editing && role !== 'instance' && (
        <form
          className={styles.form}
          noValidate
          onSubmit={(event) => {
            // Nested dialogs render inside this tree: only this form's own submit counts.
            if (event.target !== event.currentTarget) return;
            event.preventDefault();
            submit();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !needsDecision) {
              event.preventDefault();
              event.stopPropagation();
              setEditing(false);
            }
          }}
        >
          <RecurrenceFields
            draft={draft}
            onChange={(next) => {
              setDraft(next);
              setErrors([]);
            }}
            errors={errors}
            timezone={timezone}
            locale={i18n.language}
          />
          {mode === 'series' && rule && (
            <fieldset className={styles.templateGrid} data-testid="recurrence-template">
              <legend className={styles.note}>{t('recurrence.template.heading')}</legend>
              <div className={styles.fullRow}>
                <TextField
                  label={t('recurrence.template.title')}
                  value={template.title}
                  maxLength={500}
                  onChange={(event) => {
                    setTemplate({ ...template, title: event.target.value });
                  }}
                />
              </div>
              {rule.is_event && (
                <TimeField
                  label={t('recurrence.template.startTime')}
                  value={template.startTime}
                  onChange={(value) => {
                    setTemplate({ ...template, startTime: value });
                  }}
                />
              )}
              {rule.has_deadline && (
                <TimeField
                  label={t('recurrence.template.endTime')}
                  value={template.endTime}
                  onChange={(value) => {
                    setTemplate({ ...template, endTime: value });
                  }}
                />
              )}
              {rule.is_event && (
                <TextField
                  label={t('recurrence.template.length')}
                  type="number"
                  min={0}
                  max={3650}
                  value={template.length}
                  onChange={(event) => {
                    setTemplate({ ...template, length: event.target.value });
                  }}
                />
              )}
              {rule.is_event && (
                <div className={styles.fullRow}>
                  <OverdueToggle
                    value={template.overdue}
                    onChange={(overdue) => {
                      setTemplate({ ...template, overdue });
                    }}
                  />
                </div>
              )}
              <p className={`${styles.note} ${styles.fullRow}`}>{t('recurrence.template.hint')}</p>
            </fieldset>
          )}
          <div className={styles.actions}>
            <Button type="submit" size="sm" variant="primary" disabled={save.isPending}>
              {t('recurrence.save')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditing(false);
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      )}
      <FormError error={error} />
      {needsDecision && (
        <ExceptionPolicyDialog
          count={policy?.count ?? 0}
          preview={preview.data}
          conflicts={conflicts}
          pending={save.isPending}
          onChoose={(choice) => {
            if (policy) submit({ ...policy.request, exceptions: choice });
          }}
          onResolve={(dates) => {
            if (policy) {
              submit({ ...policy.request, exceptions: 'preserve', resetDates: dates });
            }
          }}
          onCancel={() => {
            setPolicy(null);
            setConflicts(null);
            save.reset();
          }}
        />
      )}
      {confirmStop && (
        <ConfirmDialog
          title={t('recurrence.stopTitle')}
          message={t('recurrence.stopMessage')}
          confirmLabel={t('recurrence.stop')}
          pending={stop.isPending}
          onCancel={() => {
            setConfirmStop(false);
          }}
          onConfirm={() => {
            stop.mutate(undefined, {
              onSettled: () => {
                setConfirmStop(false);
              },
            });
          }}
        />
      )}
      {confirmBulkDelete && (
        <BulkRecurrenceDeleteDialog
          sourceCardId={card.id}
          scope="series"
          onClose={() => {
            setConfirmBulkDelete(false);
          }}
          onDone={() => {
            setConfirmBulkDelete(false);
          }}
        />
      )}
      {instanceBulkScope && rule && role === 'instance' && (
        <BulkRecurrenceDeleteDialog
          sourceCardId={rule.source_card_id}
          scope={instanceBulkScope}
          originalDate={
            instanceBulkScope === 'following' ? (info.data.original_date ?? undefined) : undefined
          }
          onClose={() => {
            setInstanceBulkScope(null);
          }}
          onDone={() => {
            setInstanceBulkScope(null);
            onBulkDeleted?.();
          }}
        />
      )}
      {bulkEditOpen && role === 'source' && rule && (
        <BulkRecurrenceEditDialog
          sourceCardId={card.id}
          rule={rule}
          onClose={() => {
            setBulkEditOpen(false);
          }}
          onDone={() => {
            setBulkEditOpen(false);
          }}
        />
      )}
      {followingEditOpen && role === 'instance' && rule && info.data.original_date && (
        <FollowingGeneratedEditDialog
          rule={rule}
          originalDate={info.data.original_date}
          onClose={() => {
            setFollowingEditOpen(false);
          }}
          onDone={() => {
            setFollowingEditOpen(false);
            onBulkDeleted?.();
          }}
        />
      )}
    </>,
  );
}

function RecurrenceExceptions({
  cardId,
  count,
  canEdit,
}: {
  cardId: string;
  count: number;
  canEdit: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const exceptions = useOccurrenceExceptions(cardId, expanded);
  const reset = useResetOccurrence(cardId);

  return (
    <div className={styles.exceptionPanel}>
      <Button
        size="sm"
        variant="ghost"
        aria-expanded={expanded}
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        {t('occurrence.cancelledList')} ({count})
      </Button>

      {expanded && (
        <>
          {exceptions.isPending ? (
            <LoadingState />
          ) : exceptions.isError ? (
            <ErrorState
              error={exceptions.error}
              onRetry={() => {
                void exceptions.refetch();
              }}
            />
          ) : (
            <ul className={styles.list}>
              {exceptions.data.map((item) => (
                <li key={item.id} className={styles.exceptionItem}>
                  <span>
                    {t(item.cancelled ? 'occurrence.cancelledItem' : 'occurrence.changedItem', {
                      date: formatCalendarDate(item.original_date, i18n.language),
                    })}
                  </span>

                  {canEdit && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={reset.isPending}
                      onClick={() => {
                        reset.mutate(item.original_date);
                      }}
                    >
                      {t(item.cancelled ? 'occurrence.restoreOne' : 'occurrence.reset')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <FormError error={reset.error} />
        </>
      )}
    </div>
  );
}

function todayLocal(timezone: string | null): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timezone && { timeZone: timezone }),
  }).format(new Date());
}
